import { useCallback, useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../store";
import {
  clearChannelUnreadCount,
  setChannelUnreadCount,
  setConnectionStatus,
  setTotalUnreadCount,
  setInitializationStatus,
  setChatError,
} from "../store/slice/chat";
import { UnreadMessageCount } from "@sendbird/chat/groupChannel";
import { UserEventHandler } from "@sendbird/chat";
import {
  SendBirdInstance,
  SendBirdSDKUtils,
  SendBirdErrorHandler,
  ChannelHandler,
  ConnectionHandler,
} from "../utils/sendbirdSDKUtils";

const HANDLER_ID = "UNREAD_COUNT_HANDLER";
const CONNECTION_HANDLER_ID = "UNREAD_CONNECTION_HANDLER";
const USER_EVENT_HANDLER_ID = "UNREAD_USER_EVENT_HANDLER";

export const useSendBirdUnreadCount = (sb: SendBirdInstance | undefined) => {
  const dispatch = useAppDispatch();
  const currentChannelUrl = useAppSelector(
    (state) => state.chat.currentChannelUrl,
  );
  const isConnected = useAppSelector((state) => state.chat.isConnected);
  const initializationStatus = useAppSelector((state) => state.chat.initializationStatus);
  const updateTimeoutRef = useRef<NodeJS.Timeout>();
  const isMountedRef = useRef(true);
  
  // Cleanup flag to prevent setState on unmounted components
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Initialize SendBird status when hook mounts
  useEffect(() => {
    if (SendBirdSDKUtils.isValidInstance(sb) && initializationStatus === 'idle') {
      dispatch(setInitializationStatus('loading'));
    }
  }, [sb, initializationStatus, dispatch]);

  // 전체 unread count 업데이트
  const updateTotalUnreadCount = useCallback(async () => {
    if (!SendBirdSDKUtils.isValidInstance(sb) || !isMountedRef.current) return;

    // 타임아웃 방지를 위한 디바운싱
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }

    updateTimeoutRef.current = setTimeout(async () => {
      if (!isMountedRef.current) return;
      
      try {
        const unreadCount = await SendBirdErrorHandler.withErrorHandling(
          () => SendBirdSDKUtils.getTotalUnreadCount(sb),
          'updateTotalUnreadCount',
          0
        );

        if (isMountedRef.current) {
          dispatch(setTotalUnreadCount(unreadCount));
          dispatch(setChatError(null)); // Clear any previous errors
          if (initializationStatus === 'loading') {
            dispatch(setInitializationStatus('succeeded'));
          }
        }
      } catch (error) {
        if (isMountedRef.current) {
          dispatch(setChatError(error instanceof Error ? error.message : 'Unknown error'));
          dispatch(setInitializationStatus('failed'));
        }
      }
    }, 100);
  }, [sb, dispatch, initializationStatus]);

  // 특정 채널의 unread count 업데이트
  const updateChannelUnreadCount = useCallback(
    (channelUrl: string, count: number) => {
      dispatch(setChannelUnreadCount({ channelUrl, count }));
    },
    [dispatch],
  );

  // 채널 읽음 처리
  const markChannelAsRead = useCallback(
    async (channelUrl: string) => {
      if (!SendBirdSDKUtils.isValidInstance(sb) || !channelUrl || !isMountedRef.current) return;

      const success = await SendBirdErrorHandler.withErrorHandling(
        async () => await SendBirdSDKUtils.markChannelAsRead(sb, channelUrl),
        'markChannelAsRead',
        false
      );

      if (success && isMountedRef.current) {
        dispatch(clearChannelUnreadCount(channelUrl));
        dispatch(setChatError(null)); // Clear any previous errors
        void updateTotalUnreadCount();
      }
    },
    [sb, dispatch, updateTotalUnreadCount],
  );

  // SendBird 이벤트 핸들러 설정
  useEffect(() => {
    if (!SendBirdSDKUtils.isValidInstance(sb) || !isMountedRef.current) return;

    // Channel Handler with type safety
    const channelHandler: ChannelHandler = {
      onMessageReceived: (channel, _message) => {
        if (!isMountedRef.current) return;
        // 현재 활성 채널이 아닌 경우에만 unread count 증가
        if (channel.url !== currentChannelUrl) {
          updateChannelUnreadCount(channel.url, channel.unreadMessageCount);
          void updateTotalUnreadCount();
        }
      },
      onMessageUpdated: (channel, _message) => {
        if (!isMountedRef.current) return;
        updateChannelUnreadCount(channel.url, channel.unreadMessageCount);
        void updateTotalUnreadCount();
      },
      onMessageDeleted: (channel, _messageId) => {
        if (!isMountedRef.current) return;
        updateChannelUnreadCount(channel.url, channel.unreadMessageCount);
        void updateTotalUnreadCount();
      },
      onReadReceiptUpdated: (channel) => {
        if (!isMountedRef.current) return;
        updateChannelUnreadCount(channel.url, channel.unreadMessageCount);
        void updateTotalUnreadCount();
      },
      onChannelChanged: (channel) => {
        if (!isMountedRef.current) return;
        updateChannelUnreadCount(channel.url, channel.unreadMessageCount);
        void updateTotalUnreadCount();
      },
      onChannelDeleted: (channelUrl, _channelType) => {
        if (!isMountedRef.current) return;
        dispatch(setChannelUnreadCount({ channelUrl, count: 0 }));
        void updateTotalUnreadCount();
      },
    };

    // Connection Handler with type safety
    const connectionHandler: ConnectionHandler = {
      onReconnectStarted: () => {
        if (!isMountedRef.current) return;
        dispatch(setConnectionStatus(false));
      },
      onReconnectSucceeded: () => {
        if (!isMountedRef.current) return;
        dispatch(setConnectionStatus(true));
        void updateTotalUnreadCount();
      },
      onReconnectFailed: () => {
        if (!isMountedRef.current) return;
        dispatch(setConnectionStatus(false));
      },
    };

    // 핸들러 등록 with SDK compatibility
    SendBirdSDKUtils.addChannelHandler(sb, HANDLER_ID, channelHandler);
    SendBirdSDKUtils.addConnectionHandler(sb, CONNECTION_HANDLER_ID, connectionHandler);

    // User event handler with error handling
    const userEventHandler = new UserEventHandler({
      onTotalUnreadMessageCountChanged: (unreadMessageCount: UnreadMessageCount) => {
        if (!isMountedRef.current) return;
        dispatch(setTotalUnreadCount(unreadMessageCount.groupChannelCount));
      },
    });

    SendBirdSDKUtils.addUserEventHandler(sb, USER_EVENT_HANDLER_ID, userEventHandler);

    // 초기 unread count 로드
    if (isMountedRef.current) {
      void updateTotalUnreadCount();
      dispatch(setConnectionStatus(true));
    }

    // Cleanup
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
      
      const handlerIds = [HANDLER_ID, CONNECTION_HANDLER_ID, USER_EVENT_HANDLER_ID];
      SendBirdSDKUtils.removeHandlers(sb, handlerIds);
    };
  }, [
    sb,
    currentChannelUrl,
    dispatch,
    updateTotalUnreadCount,
    updateChannelUnreadCount,
    initializationStatus,
  ]);

  return {
    updateTotalUnreadCount,
    markChannelAsRead,
    isConnected,
    initializationStatus,
  };
};
